import _ from "lodash";
import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { makeStyles } from "@material-ui/styles";
import { Box, Grid, TextField } from "@material-ui/core";
import { useAlert } from "react-alert";

import CloseIcon from "@material-ui/icons/Close";

import EditIcon from "@material-ui/icons/Code";
import { CodeEditorModal } from "./CodeEditorModal";
import AutocompletePropField from "./AutocompletePropField";
import { isNetworkFilter, isNetworkFilterExistsOp } from "./util";
import AutoCompleteMultiSelectField from "./AutocompleteMultiSelectField";
import useFeatureFlags from "./hooks/useFeatureFlag";

// FilterList - Renders the list of filters. setState is a callback that will be called
// with a function that goes from (filters) => newFilters. This is used instead
// of a straightforward onChange callback so we can memoize the filter list.
//
// eventDef library, eventType determine options and enablement for Property, NW Path, Operator, Value.
//  These are optional (e.g., not required from Live View).
function FilterList({ library, eventType, filters, setState }) {
  const handleFilterChange = useCallback(
    function handleFilterChange(index, filter) {
      setState((filters) => {
        const newList = filters.slice();
        if (!isNetworkFilter(filter)) {
          filter.nw_path = "";
          // Must precede ref below to isNetworkFilterExistsOp
          if (isNetworkFilterExistsOp(filter)) {
            filter.op = "";
          }
        }
        if (isNetworkFilterExistsOp(filter)) {
          filter.val = "";
        }
        newList[index] = filter;
        return newList;
      });
    },
    [setState]
  );

  const handleFilterDeletion = useCallback(
    function handleFilterDeletion(index) {
      setState((filters) => {
        const newFilters = filters.slice();
        newFilters.splice(index, 1);
        return newFilters;
      });
    },
    [setState]
  );

  if (typeof filters == "undefined") {
    filters = [];
  }

  const renderedFilters = filters.map((filter, index) => {
    return (
      <Filter
        key={index}
        library={library}
        eventType={eventType}
        index={index}
        filter={filter}
        onChange={handleFilterChange}
        onDelete={handleFilterDeletion}
      />
    );
  });

  return <>{renderedFilters}</>;
}

FilterList.propTypes = {
  filters: PropTypes.array.isRequired,
  setState: PropTypes.func.isRequired,
  library: PropTypes.string,
  eventType: PropTypes.string,
};

const filterStyles = makeStyles(() => ({
  filter: {
    marginBottom: 16,
  },
  closeIcon: {
    alignSelf: "center",
    "&:hover": {
      cursor: "pointer",
    },
  },
  bold: {
    fontWeight: 550,
  },
}));

// Filter - Renders a filter.
//
// eventDef library, eventType determine options and enablement for Property, NW Path, Operator, Value.
//  These are optional (e.g., not required from Live View).
const Filter = React.memo(function Filter({
  library,
  eventType,
  filter,
  index,
  onChange,
  onDelete,
}) {
  const classes = filterStyles();
  const alert = useAlert();
  const flag = useFeatureFlags();

  const [codeEditorModalOpen, setCodeEditorModalOpen] = useState(false);

  const codeEditorPrompt =
    `Enter a dot-notation path.\n` +
    `\n` +
    ` Examples:\n` +
    `   .$request_headers.x-reddit-session\n` +
    `   .$response.purch_info.amt\n` +
    `   .$query_string_params.region\n` +
    `\n`;

  function handlePropertyChanges(newProps) {
    if (
      !hasNetworkEventEditPerms() &&
      newProps.prop !== undefined &&
      isNetworkFilter(newProps) &&
      !isNetworkFilter(filter)
    ) {
      alert.error(
        "You do not have permissions to create Network-specific filters."
      );
      return;
    }
    const newFilter = _.extend({}, filter, newProps);
    onChange(index, newFilter);
  }

  function showNwPathCol() {
    let isUnknownLibrary = !library;

    return (
      (isUnknownLibrary && hasNetworkEventEditPerms()) ||
      eventType === "network"
    );
  }

  function enableNwPathCol() {
    return isNetworkFilter(filter) && hasNetworkEventEditPerms();
  }

  function hasNetworkEventEditPerms() {
    return flag("networkEventDefs");
  }

  function handleCodeEditorModalSave(value) {
    handlePropertyChanges({ nw_path: value });
    setCodeEditorModalOpen(false);
  }

  function handleCodeEditorModalCancel() {
    setCodeEditorModalOpen(false);
  }

  const filtPropTypes = getFilterUiPropertyTypes(
    library,
    eventType,
    hasNetworkEventEditPerms(),
    filter
  );

  return (
    <>
      <Grid
        container
        spacing={1}
        className={classes.filter}
        id="filter-list-wrapper"
      >
        <Grid item xs={3}>
          <TextField
            fullWidth
            label="Property"
            name="prop-name"
            onChange={(e) => handlePropertyChanges({ prop: e.target.value })}
            select
            InputLabelProps={{ shrink: true }}
            SelectProps={{ native: true }}
            value={filter.prop}
            variant="outlined"
            className={clsx(filter.prop === "" && classes.bold)}
            disabled={isNetworkFilter(filter) && !hasNetworkEventEditPerms()}
          >
            <option value="" hidden>
              Property
            </option>
            <FilterUiPropertyList filtPropTypes={filtPropTypes} />
          </TextField>
        </Grid>
        {showNwPathCol() && (
          <>
            <Grid item xs style={{ position: "relative" }}>
              <AutocompletePropField
                propName={filter.prop}
                fullWidth
                label="NW Path"
                name="nw_path"
                onChange={(value) => handlePropertyChanges({ nw_path: value })}
                value={filter.nw_path}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                disabled={!enableNwPathCol()}
              />
              {isNetworkFilter(filter) && (
                <Box
                  component="button"
                  style={{
                    position: "absolute",
                    right: 16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                  }}
                >
                  <EditIcon onClick={() => setCodeEditorModalOpen(true)} />
                </Box>
              )}
            </Grid>
          </>
        )}
        <Grid item xs={3}>
          <TextField
            fullWidth
            label="Operator"
            name="op-name"
            onChange={(e) => {
              const operator = e.target.value;
              let filterVal = filter.val;
              // NOTE(bianchi): different operators expect different value types - this code ensures that the value
              // object is the appropriate type for the operator
              if (operator === "any_of") {
                if (typeof filter.val === "string" && filter.val !== "") {
                  filterVal = [filter.val];
                } else {
                  // if it's not a simple string -> array, we just reset to an empty array to be safe
                  filterVal = [];
                }
              } else {
                if (typeof filter.val !== "string") {
                  filterVal = "";
                }
              }
              if (operator[0] === "!") {
                handlePropertyChanges({
                  op: operator.slice(1),
                  negate: true,
                  val: filterVal,
                });
              } else {
                handlePropertyChanges({
                  op: operator,
                  negate: false,
                  val: filterVal,
                });
              }
            }}
            select
            InputLabelProps={{ shrink: true }}
            SelectProps={{ native: true }}
            value={filter.negate ? "!" + filter.op : filter.op}
            variant="outlined"
            className={clsx(filter.op === "" && classes.bold)}
            disabled={isNetworkFilter(filter) && !hasNetworkEventEditPerms()}
          >
            <option value="" hidden>
              Operator
            </option>
            <option value="contains">Contains</option>
            <option value="wildcard_matches">Matches</option>
            <option value="=">Equals</option>

            <option value="!contains">Does Not Contain</option>
            <option value="!wildcard_matches">Does Not Match</option>
            <option value="!=">Does Not Equal</option>
            <option value="any_of">Any Of</option>
            {isNetworkFilter(filter) && (
              <>
                <option value="exists">Exists</option>
                <option value="!exists">Does Not Exist</option>
              </>
            )}
          </TextField>
        </Grid>
        <Grid item xs>
          {filter.op !== "any_of" ? (
            <AutocompletePropField
              propName={filter.prop}
              fullWidth
              label="Value"
              name="value"
              onChange={(value) => handlePropertyChanges({ val: value })}
              value={filter.val}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              disabled={
                isNetworkFilterExistsOp(filter) ||
                (isNetworkFilter(filter) && !hasNetworkEventEditPerms())
              }
            />
          ) : (
            <AutoCompleteMultiSelectField
              propName={filter.prop}
              fullWidth
              label="Value"
              name="value"
              onChange={(value) => {
                handlePropertyChanges({ val: value });
              }}
              value={filter.val}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              disabled={
                isNetworkFilterExistsOp(filter) ||
                (isNetworkFilter(filter) && !hasNetworkEventEditPerms())
              }
            />
          )}
        </Grid>
        <Grid className={clsx(classes.closeIcon)} item xs={1}>
          <CloseIcon onClick={() => onDelete(index)} />
        </Grid>
      </Grid>
      {codeEditorModalOpen && (
        <CodeEditorModal
          onCancel={handleCodeEditorModalCancel}
          onSave={handleCodeEditorModalSave}
          parentElementID="filter-list-wrapper"
          initialCode={filter.nw_path}
          textPrompt={codeEditorPrompt}
          saveEnabled={enableNwPathCol()}
        />
      )}
    </>
  );
});

Filter.propTypes = {
  filter: PropTypes.object.isRequired,
  library: PropTypes.string,
  eventType: PropTypes.string,
  index: PropTypes.number,
  onChange: PropTypes.func,
  onDelete: PropTypes.func,
};

export default FilterList;

export function getFilterUiPropertyTypes(
  library,
  eventType,
  hasNetworkEventEditPerms,
  filter
) {
  let filtPropTypes = [];
  let isNetworkEventDef = eventType === "network";
  let isWebLibrary = library === "web";
  let isUnknownLibrary = !library;

  // On web, we've replaced $el_text with $el_nested_text which handles
  //   many situations better. If they are using web and were not
  //   previously using $el_text, we use $el_nested_text. Otherwise use
  //   the older $el_text.
  let useNestedTargetText = library === "web" && filter.prop !== "$el_text";

  if (!isNetworkEventDef) {
    filtPropTypes.push({ display: "Browser", key: "$browser" });
    filtPropTypes.push({ display: "Device ID", key: "$device_id" });
  }
  filtPropTypes.push({ display: "Domain", key: "$host" });
  if (!isNetworkEventDef) {
    filtPropTypes.push({ display: "Href", key: "$el_attr__href" });
  }
  filtPropTypes.push({ display: "Full URL", key: "$current_url" });
  if (!isNetworkEventDef) {
    filtPropTypes.push({ display: "Path", key: "$pathname" });
  }
  filtPropTypes.push({ display: "IP", key: "$ip" });
  if (!isNetworkEventDef) {
    if (useNestedTargetText) {
      filtPropTypes.push({ display: "Target Text", key: "$el_nested_text" });
    } else {
      filtPropTypes.push({ display: "Target Text", key: "$el_text" });
    }
  }
  filtPropTypes.push({ display: "User ID", key: "$user_id" });
  if (!isWebLibrary) {
    filtPropTypes.push({
      display: "React Native Screen Path",
      key: "$screen_path",
    });
    filtPropTypes.push({
      display: "React Native Screen Name",
      key: "$screen_name",
    });
  }
  if (isNetworkEventDef || (hasNetworkEventEditPerms && isUnknownLibrary)) {
    filtPropTypes.push({ display: "Network", key: "$network" });
  }
  return filtPropTypes;
}

const FilterUiPropertyList = React.memo(function FilterUiPropertyList({
  filtPropTypes,
}) {
  return filtPropTypes.map((filtPropType) => (
    <option key={filtPropType.key} value={filtPropType.key}>
      {filtPropType.display}
    </option>
  ));
});
